import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import StreamingCard from './StreamingCardComponent';
import Spinner from '@alphapoint/apex-web/lib/components/common/Spinner/Spinner';
import { getBEMClasses } from '../../helpers/cssClassesHelper';
import { getPriceDataForQuantitiesSelector } from '@alphapoint/apex-web/lib/redux/selectors/buySellSelectors';
import './StreamingCardGrid.css';
import { useStore } from 'react-redux';
import { InnerSelect } from '@alphapoint/apex-web/lib/components/common/APSelect/APSelect';

const streamingCardGridClasses = getBEMClasses('apg-streaming-card-grid');

const TIF_GTC = 'TIF: GTC';
const TIF_FOK = 'TIF: FOK';
const TIF_IOC = 'TIF: IOC';

const StreamingCardGrid = memo((props) => {
  const {
    changeReduxForm,
    setInstrument,
    selectedInstrumentId,
    instruments,
    getLevel1Data,
    unsubscribeLevel1,
    product2Options,
  } = props;
  const store = useStore();
  const [selectedProduct2, setSelectedProduct2] = useState('');
  const [currentPos, setCurrentPos] = useState(0);
  const [currentVal, setCurrentVal] = useState(0);
  const [defaultSelection, setDefaultSelection] = useState(() =>
    props.instruments.map(() => ({
      qty: 0,
      orderTypeValue: TIF_GTC,
      slipage: 10,
      min: 0,
      max: 0,
      newBuyPrice: '',
      newSellPrice: '',
      limitBuyPrice: '',
      limitSellPrice: '',
    })),
  );

  const [, setOrderTypeCounter] = useState(TIF_GTC);
  const [orderType] = useState([
    {
      value: TIF_GTC,
      label: TIF_GTC,
    },
    {
      value: TIF_FOK,
      label: TIF_FOK,
    },
    {
      value: TIF_IOC,
      label: TIF_IOC,
    },
  ]);

  const selectOrderType = (position, e) => {
    let newSelection = {
      ...defaultSelection,
    };
    newSelection[position].orderTypeValue = e.target.value;
    newSelection[position].slipage = 10;

    setDefaultSelection(newSelection);
    setOrderTypeCounter(e.target.value);

    if (e.target.value == TIF_GTC) {
      setTimeout(function () {
        setDefaultSlippage(0);
      }, 100);
    } else {
      setTimeout(function () {
        setDefaultSlippage(10);
      }, 100);
    }
  };

  const clearForm = () => {
    let result = Object.assign({}, defaultSelection);
    for (let [key] of Object.entries(result)) {
      result[key].qty = 0;
    }
    setCurrentPos(-1);
    setCurrentVal(-1);
    setDefaultSelection(result);
    props.reset();
  };

  const setQuantityDataForSelection = useCallback(
    (selectedPosition, selectedValue) => {
      if (currentPos != -1) {
        setDefaultSelection((defaultSelection) => {
          const instrumentId = instruments[selectedPosition]?.InstrumentId;
          let newSelection = {
            ...defaultSelection,
          };

          if (selectedPosition != currentPos && newSelection[currentPos]) {
            changeReduxForm({ field: 'quantity-' + currentPos, value: 0 });
            newSelection[currentPos].qty = 0;
            newSelection[currentPos].newBuyPrice = '';
            newSelection[currentPos].newSellPrice = '';
            newSelection[currentPos].limitBuyPrice = '';
            newSelection[currentPos].limitSellPrice = '';
          }

          if (instrumentId != selectedInstrumentId) {
            setInstrument({
              selectedInstrumentId: selectedInstrumentId,
              instrumentId: instrumentId,
            });
          }

          const state = store.getState();
          const newBuyPrice = getPriceDataForQuantitiesSelector(state, '0', [parseFloat(selectedValue)]);
          const newSellPrice = getPriceDataForQuantitiesSelector(state, '1', [parseFloat(selectedValue)]);

          if (newSelection[selectedPosition]) {
            newSelection[selectedPosition].qty = parseFloat(selectedValue);
            newSelection[selectedPosition].newBuyPrice =
              newBuyPrice[0].pricePerUnit != null ? newBuyPrice[0].pricePerUnit : '';
            newSelection[selectedPosition].newSellPrice =
              newSellPrice[0].pricePerUnit != null ? newSellPrice[0].pricePerUnit : '';
            newSelection[selectedPosition].limitBuyPrice =
              newBuyPrice[0].limitPrice != null ? newBuyPrice[0].limitPrice : '';
            newSelection[selectedPosition].limitSellPrice =
              newSellPrice[0].limitPrice != null ? newSellPrice[0].limitPrice : '';
          }
          return newSelection;
        });
      }
    },
    [currentPos, instruments, selectedInstrumentId, changeReduxForm, store, setInstrument],
  );

  useEffect(() => {
    if (product2Options.length > 0 && !selectedProduct2) {
      setSelectedProduct2(product2Options[0].value);
    }
  }, [product2Options, selectedProduct2]);

  useEffect(() => {
    if (props.formData && props.formData.active && props.formData.active.includes('slipage')) {
      const selectedPosition = props.formData.active.split('-')[1];
      const selectedValue = props.formData.values ? props.formData.values[props.formData.active] : 0;

      if (selectedValue > 0) {
        setDefaultSelection((defaultSelection) => {
          const newSelection = {
            ...defaultSelection,
          };
          newSelection[selectedPosition].slipage = parseFloat(selectedValue);
          return newSelection;
        });
      }
    } else if (props.formData && props.formData.active && props.formData.active.includes('quantity')) {
      const selectedPosition = props.formData.active.split('-')[1];
      const selectedValue = props.formData.values ? props.formData.values[props.formData.active] : 0;

      setCurrentPos(selectedPosition);
      setCurrentVal(selectedValue);

      setQuantityDataForSelection(selectedPosition, selectedValue);
    }
  }, [props.formData, setQuantityDataForSelection]);

  useEffect(() => {
    setQuantityDataForSelection(currentPos, currentVal);
  }, [props.level1, currentPos, currentVal, setQuantityDataForSelection]);

  useEffect(() => {
    instruments.forEach((inst) => {
      getLevel1Data(inst.InstrumentId);
    });

    return () => {
      instruments
        .filter((i) => i.InstrumentId !== selectedInstrumentId)
        .forEach((inst) => {
          unsubscribeLevel1(inst.InstrumentId);
        });
    };
  }, [instruments, selectedInstrumentId, unsubscribeLevel1, getLevel1Data]);

  const setDefaultSlippage = (value) => {
    props.instruments.map((item, position) => {
      props.changeReduxForm({ field: `slipage-${position}`, value: value });
    });
  };

  const filteredInstruments = useMemo(() => {
    return props.instruments.filter((item) => {
      return item.Product2 === Number(selectedProduct2);
    });
  }, [props.instruments, selectedProduct2]);

  const items = filteredInstruments.map((item, position) => {
    return (
      <StreamingCard
        key={position}
        defaultSelection={defaultSelection}
        position={position}
        instrument={item}
        formData={props.formData}
        products={props.products}
        level1={props.level1}
        productManifest={props.productManifest}
        orderType={orderType}
        selectOrderType={selectOrderType}
        AccountId={props.AccountId}
        showNotification={props.showNotification}
        clearForm={clearForm}
        selectedInstrumentId={props.selectedInstrumentId}
        additionalSlipage={props.additionalSlipage}
      />
    );
  });

  return (
    <React.Fragment>
      {props.fetching || !!items.length ? (
        props.fetching ? (
          <Spinner />
        ) : (
          <div className={streamingCardGridClasses('wrapper')}>
            <div className={streamingCardGridClasses('select')}>
              <InnerSelect
                name={'selectedProduct2'}
                options={props.product2Options}
                placeholder={'Select Instrument'}
                showTriangles={true}
                onSelect={setSelectedProduct2}
              />
            </div>
            <div className={streamingCardGridClasses('')}>{items}</div>
          </div>
        )
      ) : (
        ''
      )}
    </React.Fragment>
  );
});
StreamingCardGrid.displayName = 'StreamingCardGrid';

export default StreamingCardGrid;
